import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, Users, Award, Clock, Shield, Target, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebDesignAgency = () => {
  const faqs = [
    {
      question: "What does a landscaping web design agency do differently?",
      answer: "A landscaping web design agency understands hardscape projects, outdoor living, and design-build sales cycles. We create portfolio-driven sites that showcase your best work and convert high-ticket leads—not generic template sites."
    },
    {
      question: "How much should I expect to pay a landscaping web design agency?",
      answer: "Agencies typically charge $2,000–$15,000+ for one-time builds or $200–$500/month for subscription models. Premium landscaping portfolios can cost more due to gallery complexity and project showcases."
    },
    {
      question: "Do landscaping agencies understand hardscape and outdoor living?",
      answer: "Niche agencies do. We build sites for patios, retaining walls, outdoor kitchens, fire pits, and full landscape design. General agencies often lump landscaping with lawn care without understanding the differences."
    },
    {
      question: "How long does a landscaping website take to build with an agency?",
      answer: "General agencies take 4–12 weeks. We typically deliver in 10–14 days because we specialize in this niche and have proven processes for landscaping portfolios and service pages."
    },
    {
      question: "Should I hire a local agency or a specialized national agency?",
      answer: "Specialization matters more than location. A national agency focused on landscaping understands your industry better than a local generalist. All communication happens remotely anyway."
    },
    {
      question: "Can the agency help showcase my best landscaping projects?",
      answer: "Yes. A good landscaping web design agency creates project galleries with before/after photos, project details, and calls-to-action that help visitors visualize working with you."
    },
    {
      question: "Will my landscaping website rank in Google?",
      answer: "A quality agency builds SEO into the foundation—service area pages, proper headings, schema markup, and fast loading. Ongoing ranking depends on content quality and local competition."
    },
    {
      question: "What should I prepare before hiring a landscaping web design agency?",
      answer: "Gather high-quality project photos (before/after if possible), your service list, service areas, and any testimonials or reviews. The agency handles everything else—copywriting, design, and SEO structure."
    },
    {
      question: "Do agencies offer ongoing maintenance for landscaping sites?",
      answer: "Good agencies do. Our subscription model includes hosting, maintenance, security updates, and content changes. You're never stuck with an outdated site."
    },
    {
      question: "How do I evaluate if a landscaping agency is right for me?",
      answer: "Ask for landscaping-specific portfolio examples. If they can't show hardscape, patio, or outdoor living projects, they're not specialized enough. A discovery call should focus on your business, not just packages."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-web-design-agency/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Landscaping web design agency specializing in websites for landscaping and hardscape companies."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-web-design-agency/#webpage",
    "name": "Landscaping Web Design Agency | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-web-design-agency/",
    "description": "Niche landscaping web design agency for hardscapers and landscapers. Portfolio-focused websites that close high-ticket jobs. $0 upfront.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Landscaping Web Design Agency | Leaf Rush"
        description="Niche landscaping web design agency for hardscapers and landscapers. Portfolio-focused websites that close high-ticket jobs. $0 upfront."
        keywords="landscaping web design agency, landscaping website agency, landscaper digital agency, landscaping website company"
        canonical="https://www.leafrushmarketing.com/landscaping-web-design-agency/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-web-design-agency/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Portfolio-Focused Agency
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Web Design Agency That Showcases Your Best Work
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                We're a landscaping web design agency that builds portfolio-driven websites for hardscapers, landscape designers, and outdoor living specialists.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    A landscaping web design agency specializes in building websites that showcase hardscape, outdoor living, and landscape design projects. Expect to pay $200–$500/month or $2,000–$15,000 one-time for portfolio-focused sites.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're exploring all your options, visit our main{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                  landscaping website design
                </NavLink>{" "}
                page for a complete overview of what we build.
              </div>
            </div>
          </div>
        </section>

        {/* Why Portfolio Matters */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Landscapers Need a Specialized Agency
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Portfolio-First Design</p>
                    <p className="text-sm text-muted-foreground">
                      High-ticket landscaping jobs close on visual proof. We build galleries that sell your work.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Hardscape Expertise</p>
                    <p className="text-sm text-muted-foreground">
                      Patios, retaining walls, outdoor kitchens, fire pits—we know how to position each service.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Award className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">High-Ticket Focus</p>
                    <p className="text-sm text-muted-foreground">
                      Our sites are built to attract $10K–$100K+ projects, not just small maintenance jobs.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Ongoing Support</p>
                    <p className="text-sm text-muted-foreground">
                      Add new projects, update service areas, seasonal changes—we handle it all.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Build */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What Our Agency Builds for Landscapers
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Project Portfolios</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Before/after galleries, project details, and visual proof that helps close high-ticket sales.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Service Pages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Patios, retaining walls, outdoor kitchens, plantings—each service gets its own optimized page.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Local SEO</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Service area pages, schema markup, and on-page optimization to rank where you work.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground mb-4">
                  See our{" "}
                  <NavLink to="/landscaping-website-examples/" className="text-primary underline hover:no-underline">
                    landscaping website examples
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to="/landscaping-portfolio-website/" className="text-primary underline hover:no-underline">
                    landscaping portfolio website guide
                  </NavLink>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Agency vs Alternatives */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Agency vs. Other Options
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Cost</th>
                      <th className="text-left py-3 px-4">Timeline</th>
                      <th className="text-left py-3 px-4">Portfolio Focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Niche Agency (Us)</td>
                      <td className="py-3 px-4">$200–$300/mo</td>
                      <td className="py-3 px-4">10–14 days</td>
                      <td className="py-3 px-4">High</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">General Agency</td>
                      <td className="py-3 px-4">$5,000–$15,000+</td>
                      <td className="py-3 px-4">4–12 weeks</td>
                      <td className="py-3 px-4">Variable</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Freelancer</td>
                      <td className="py-3 px-4">$2,000–$8,000</td>
                      <td className="py-3 px-4">3–8 weeks</td>
                      <td className="py-3 px-4">Variable</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">DIY Builder</td>
                      <td className="py-3 px-4">$0–$300/yr</td>
                      <td className="py-3 px-4">Weeks–Months</td>
                      <td className="py-3 px-4">Low</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Compare options:{" "}
                <NavLink to="/landscaping-website-cost/" className="text-primary underline hover:no-underline">
                  landscaping website cost breakdown
                </NavLink>{" "}
                |{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Landscaping Web Design Agencies
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/landscaping-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional landscaping websites.</p>
                </NavLink>
                <NavLink to="/landscaping-website-cost/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Cost</h3>
                  <p className="text-sm text-muted-foreground">Compare pricing across all options.</p>
                </NavLink>
                <NavLink to="/landscaping-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for landscaping sites.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready to Work With a Landscaping Web Design Agency?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Let's discuss your landscaping business and see if we're a fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/contact#get-started">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </NavLink>
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebDesignAgency;
